import React, { memo, useMemo, useRef, useState } from 'react';
import Link from 'next/link';

import FutureImage from 'next/image';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import ButtonLink from '@mui/material/Link';
import Hidden from '@mui/material/Hidden';
import { useTheme } from '@mui/material';
import { Menu, X } from 'lucide-react';

import { Drawer } from './components/drawer';
import { UserPopover } from './components/user-popover';

import { DrawerContent } from './components/drawer/content';
import { UserLoginButton } from './components/user-login-button';
import { IUserPopoverHandle } from './components/user-popover/model';

import { INavProps } from './interface';
import {
  AppHeader,
  HiddenH1,
  StyledBox,
  StyledIconButton,
  StyledToolbar,
} from './styles';
import { SearchButton } from './components/search-button';

import { NavbarPopover } from './components/navbar-popover';
import {
  LEARN_OPTION_MENU,
  LIST_OPTION_MENU,
  MAIS_RETORNO_MENU,
  PRODUCTS_MENU,
  TOOLS_OPTION_MENU,
} from './constants/menu-options.constant';
import { GeneralSearch } from '../general-search';
import { RedirectToolButton } from '../redirect-tool-button';
import { useAuth } from '@core/features-core/auth/client';
import { NavbarProductPopover } from './components/navbar-popover/products-popover';
import { GeneralSearchWithAdminAndManagersUseCase } from '@core/design-system/search';
import { UTMContent } from '../utm-button/enum';

export const Navbar = memo<INavProps>(
  ({ useH1 = false, hideUserSection = false }) => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const [isSearchOpen, setSearchOpen] = useState(false);

    const theme = useTheme();
    const popoverRef = useRef<IUserPopoverHandle>(null);
    const searchUseCase = useMemo(
      () => new GeneralSearchWithAdminAndManagersUseCase(),
      []
    );
    const { userIsPremium, userIsPro } = useAuth();

    const openPopover = (e) => {
      popoverRef.current.handleClick(e);
    };

    function handleOpenSearchModal() {
      setSearchOpen(true);
    }

    return (
      <>
        <AppHeader
          position="sticky"
          style={{ zIndex: theme.zIndex.drawer + 1 }}
        >
          <Container maxWidth={false}>
            <StyledToolbar>
              <StyledIconButton
                id="menu"
                edge="start"
                aria-label={openDrawer ? 'Fechar menu' : 'Abrir menu'}
                aria-haspopup="true"
                aria-controls="menu"
                aria-expanded={openDrawer ? 'true' : 'false'}
                onClick={() => setOpenDrawer(true)}
              >
                <Menu />
              </StyledIconButton>

              <Link href="/" passHref legacyBehavior prefetch={false}>
                <ButtonLink title="Ir para a home">
                  {useH1 && <HiddenH1> Mais Retorno </HiddenH1>}
                  <FutureImage
                    priority
                    src="/img/logo-principal.svg"
                    alt="Logo Mais Retorno"
                    title="Logo mais retorno"
                    width={130}
                    height={30}
                  />
                </ButtonLink>
              </Link>
              {!hideUserSection && (
                <StyledBox>
                  <NavbarPopover
                    title={TOOLS_OPTION_MENU.title}
                    items={[
                      ...TOOLS_OPTION_MENU.items,
                      ...LIST_OPTION_MENU.items,
                    ]}
                  />

                  <NavbarProductPopover
                    title={PRODUCTS_MENU.title}
                    items={PRODUCTS_MENU.items}
                  />

                  <NavbarPopover
                    title={LEARN_OPTION_MENU.title}
                    items={LEARN_OPTION_MENU.items}
                  />

                  <NavbarPopover
                    title={MAIS_RETORNO_MENU.title}
                    items={MAIS_RETORNO_MENU.items}
                  />

                  <Box
                    sx={{
                      display: `flex`,
                      alignItems: `center`,
                      gap: `1.5rem`,
                      ml: 2,
                    }}
                  >
                    <Box
                      display={{
                        sm: 'none',
                        xs: 'none',
                        md: 'block',
                      }}
                    >
                      <RedirectToolButton
                        utmContent={UTMContent.CTA_NAVBAR}
                        userIsPremium={userIsPremium}
                        userIsPro={userIsPro}
                        textVariant="navbar"
                        size="small"
                        showProRedirectConfirmation={false}
                      />
                    </Box>

                    <Box
                      id={popoverRef.current?.id}
                      onClick={openPopover}
                      style={{ cursor: 'pointer' }}
                      displayPrint="none"
                      sx={{
                        backgroundColor: isPopoverOpen
                          ? '#E0E0E0'
                          : 'transparent',
                        borderRadius: '50px',
                        color: isPopoverOpen
                          ? theme.palette.primary.main
                          : '#fff',
                        transition: 'background-color 0.5s, color 0.5s',
                      }}
                    >
                      <UserLoginButton id="signin" />
                    </Box>
                  </Box>
                  <SearchButton handleOpenModal={handleOpenSearchModal} />
                </StyledBox>
              )}
            </StyledToolbar>
          </Container>
        </AppHeader>

        <Drawer onClose={setOpenDrawer} openDrawer={openDrawer}>
          <Hidden smUp>
            <Box
              displayPrint="none"
              sx={{
                position: 'absolute',
                right: 0,
                zIndex: 1,
              }}
            >
              <IconButton onClick={() => setOpenDrawer(false)} size="large">
                <X size={20} color={theme.palette.primary.contrastText} />
              </IconButton>
            </Box>
          </Hidden>
          <DrawerContent setOpenDrawer={setOpenDrawer} />
        </Drawer>

        <GeneralSearch
          forwardRef={null}
          dialogMode
          isOpen={isSearchOpen}
          useCase={searchUseCase as any}
          onClose={() => setSearchOpen(false)}
          placeholder="Pesquise por fundos, ações, FII, título público, gestores, termos, vídeos e mais..."
        />

        <UserPopover setOpen={setPopoverOpen} ref={popoverRef} />
      </>
    );
  }
);
